/**
 * V2 — Pastilles "Check ELS" : PEB / ELS / Élec / Picts / SRS.
 *
 * Reprend l'info dense scannable que tu avais dans le tableau V1 Suivi ELS.
 * Pure UI : la logique de calcul est délégée aux helpers `checkElsStates()`
 * pour pouvoir réutiliser le calcul ailleurs (filtres, tris).
 */

import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { BienRow } from "@/schemas/bien";
import { isPebExpiringSoon } from "@/lib/pebCycle";
import { isElsLance } from "@/lib/bienPrepValidation";

export type BadgeState = "success" | "danger" | "warning";

export interface CheckElsState {
  peb: BadgeState;
  els: BadgeState;
  elec: BadgeState;
  picts: BadgeState;
  srs: BadgeState;
}

// ---------------------------------------------------------------------------
// Helpers métier (extraits de SuiviElsPage.tsx)
// ---------------------------------------------------------------------------

// isPebExpiringSoon est importé depuis @/lib/pebCycle (source unique)

/**
 * La colonne DB `statutEls` a été supprimée (migration 112) : elle était vide
 * sur les 1574 biens et plus aucun écrivain ne l'alimentait depuis la
 * disparition des pages V1 en juillet. Le second terme de ce OU était donc
 * toujours faux — seul `statutEle === "expertise_ok"` décidait.
 */
function isElsOk(b: BienRow): boolean {
  return b.statutEle === "expertise_ok";
}

function isTransfertElecFait(b: BienRow): boolean {
  if (b.transfertElecDate && typeof b.transfertElecDate === "string" && b.transfertElecDate.trim().length > 0) {
    return true;
  }
  return !!b.transfertElecFait;
}

/**
 * Picts (Appartement prêt pour photos) — tri-état :
 *  - Oui (true)   → success ✓  : prêt pour photos
 *  - Non (false)  → success ✓  : décision prise (pas une alerte)
 *  - Non saisi    → warning – : collab doit trancher
 *
 * Décision user 2026 : "Non" est une réponse valide, pas un blocage.
 * Le rouge était trompeur — seul l'état "pas saisi" mérite une alerte.
 */
function getPictsState(appartPretPhotos: boolean | null | undefined): BadgeState {
  if (appartPretPhotos === true || appartPretPhotos === false) return "success";
  return "warning";
}

/** SRS : dossier finalisé par le collab (dossierCloture) OU clos par C-Level. */
function isSrsOk(b: BienRow): boolean {
  return b.dossierCloture === true || b.dossierClos === true;
}

export function checkElsStates(bien: BienRow): CheckElsState {
  return {
    peb: !isPebExpiringSoon(bien.pebFinLe, bien.classePeb) ? "success" : "danger",
    els: isElsOk(bien) ? "success" : "danger",
    elec: isTransfertElecFait(bien) ? "success" : "danger",
    picts: getPictsState(bien.appartPretPhotos),
    srs: isSrsOk(bien) ? "success" : "danger",
  };
}

/** Nombre d'items NON success (pour tri/filtre/badges agrégés). */
export function checkElsIssueCount(state: CheckElsState): number {
  return (Object.values(state) as BadgeState[]).filter((s) => s !== "success").length;
}

// ---------------------------------------------------------------------------
// UI
// ---------------------------------------------------------------------------

const STATE_STYLE: Record<BadgeState, { cls: string; icon: string }> = {
  success: { cls: "bg-success/10 text-success border-success/20", icon: "✓" },
  danger:  { cls: "bg-danger/10 text-danger border-danger/20",   icon: "✗" },
  warning: { cls: "bg-warning/10 text-warning border-warning/20", icon: "–" },
};

interface BadgeProps {
  label: string;
  state: BadgeState;
  /** Taille de la pastille (default = sm). xs = très compact pour cards kanban. */
  size?: "xs" | "sm";
  title?: string;
}

function Badge({ label, state, size = "sm", title }: BadgeProps) {
  const cfg = STATE_STYLE[state];
  return (
    <span
      title={title}
      className={cn(
        "inline-flex items-center gap-0.5 font-semibold rounded border",
        size === "xs"
          ? "text-[9px] px-1 py-0.5 leading-none"
          : "text-[10px] px-1.5 py-0.5",
        cfg.cls,
      )}
    >
      <span>{cfg.icon}</span>
      <span>{label}</span>
    </span>
  );
}

interface CheckElsBadgesProps {
  bien: BienRow;
  size?: "xs" | "sm";
  /** True : affiche les libellés (PEB/ELS/Élec/Picts/SRS). False : juste icônes. */
  showLabels?: boolean;
}

export function CheckElsBadges({ bien, size = "sm", showLabels = true }: CheckElsBadgesProps) {
  const { t } = useTranslation();
  const s = checkElsStates(bien);
  // ELS / Élec / SRS n'existent que si un ELS est lancé (cycle ouvert, case
  // "ELS requis" non décochée) — plan « deux cycles » 2026-09-15. Sans ELS,
  // seuls PEB et Picts restent pertinents pour la mise en marché. Remplace
  // l'ancien test sur `entreeGestion` (bien vide), qui déduisait ça de
  // l'origine du bien au lieu du fait.
  const els = isElsLance(bien as Record<string, unknown>);
  return (
    <div className={cn("inline-flex flex-wrap", size === "xs" ? "gap-0.5" : "gap-1")}>
      <Badge label={showLabels ? t("v2.checkEls.peb") : ""} state={s.peb} size={size} title={t("v2.checkEls.pebTooltip")} />
      {els && <Badge label={showLabels ? t("v2.checkEls.els") : ""} state={s.els} size={size} title={t("v2.checkEls.elsTooltip")} />}
      {els && <Badge label={showLabels ? t("v2.checkEls.elec") : ""} state={s.elec} size={size} title={t("v2.checkEls.elecTooltip")} />}
      <Badge label={showLabels ? t("v2.checkEls.picts") : ""} state={s.picts} size={size} title={t("v2.checkEls.pictsTooltip")} />
      {els && <Badge label={showLabels ? t("v2.checkEls.srs") : ""} state={s.srs} size={size} title={t("v2.checkEls.srsTooltip")} />}
    </div>
  );
}
